import { useCallback, useEffect, useRef, useState } from 'react'
import {
  performSheetUpload,
  uploadFileToSpace,
  UploadProgress,
} from '../api/files/api.files'
import { FileUploadState } from '../api/files/api.jobs'

// Constants
const formatSpeed = (speedKBps: number) => {
  if (speedKBps < 1024) {
    return speedKBps + ' KB/s'
  } else if (speedKBps < 1024 * 1024) {
    return (speedKBps / 1024).toFixed(1) + ' MB/s'
  } else {
    return (speedKBps / (1024 * 1024)).toFixed(1) + ' GB/s'
  }
}

export interface FileUpload {
  name?: string
  progress?: number
  error?: string
  complete?: boolean
}

export type FileUploads = Record<string, FileUpload>

interface UseFileUploadAndExtractProps {
  environmentId: string
  spaceId: string
  onUploadStart?: () => void
  onUploadProgress?: (progress: UploadProgress) => void
  onUploadComplete?: (fileId: string) => void
  onUploadError?: (error: string) => void
  onExtractionStart?: () => void
  onExtractionComplete?: (fileUpload: { [id: string]: FileUpload }) => void
  onExtractionError?: (error: string) => void
  onUnsupportedFile?: () => void
  silent?: boolean
}

export const useFileUploadAndExtract = ({
  environmentId,
  spaceId,
  onUploadStart,
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  onExtractionStart,
  onExtractionComplete,
  onExtractionError,
  onUnsupportedFile,
  silent = false,
}: UseFileUploadAndExtractProps) => {
  const [fileUploads, setFileUploads] = useState<FileUploads>({})
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    progress: 0,
    speed: 0,
  })
  const [fileName, setFileName] = useState<string | undefined>()
  const fileIdRef = useRef('')
  const [fileUploadState, setFileUploadState] =
    useState<FileUploadState>('idle')
  const fileUploadStateRef = useRef<FileUploadState>('idle')

  // Keep the ref in sync with the state
  useEffect(() => {
    fileUploadStateRef.current = fileUploadState
  }, [fileUploadState])

  // Calculate upload progress
  const calculateUploadProgress = useCallback(
    (progressEvent: any, startTime: number): UploadProgress => {
      const progress = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      )
      const timeElapsed = (Date.now() - startTime) / 1000 // in seconds
      const uploadedBytes = progressEvent.loaded
      const speed = uploadedBytes / timeElapsed // bytes per second

      return {
        progress,
        speed: Math.round(speed / 1024), // Convert to KB/s
      }
    },
    []
  )

  // Handle file upload
  const handleFileUpload = useCallback(
    async (file: File, spaceIdOverride?: string) => {
      if (!file || (!spaceId && !spaceIdOverride)) {
        return
      }

      setFileName(file.name)
      setUploadProgress({
        progress: 0,
        speed: 0,
      })
      setFileUploadState('uploading')

      if (onUploadStart) {
        onUploadStart()
      }

      try {
        const fileResult = (await uploadFileToSpace(
          file,
          (progress) => {
            setUploadProgress(progress)
            if (onUploadProgress) {
              onUploadProgress(progress)
            }
          },
          calculateUploadProgress,
          environmentId,
          spaceIdOverride ?? spaceId
        )) as any

        const fileId = fileResult.id
        // Only perform sheet upload for Excel files, not CSV
        // CSV files are extracted by the backend automatically
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          await performSheetUpload(file, fileId, spaceIdOverride ?? spaceId)
        }

        fileIdRef.current = fileId

        if (onUploadComplete) {
          onUploadComplete(fileResult.id)
        }

        // Update file uploads state
        const updatedFileUploads = { ...fileUploads }
        updatedFileUploads[fileResult.id] = {
          ...updatedFileUploads[fileResult.id],
          name: file.name,
          complete: true,
        }
        setFileUploads(updatedFileUploads)

        // TODO: TEMPORARY SOLUTION - This is a temporary fix until we properly connect to job events
        // Currently marking extraction as complete immediately after upload finishes
        // This should be reverted once job event listeners are properly implemented
        setFileUploadState('idle')
        if (onExtractionComplete) {
          const addedFileUpload = {
            [fileResult.id]: updatedFileUploads[fileResult.id],
          }
          onExtractionComplete(addedFileUpload)
        }

        // Comment out the extraction process for now
        /*
        // Start extraction process
        setFileUploadState('waiting')
        if (onExtractionStart) {
          onExtractionStart()
        }

        // Trigger extraction timer
        await triggerExtractionTimer(
          fileResult.id,
          fileUploadStateRef.current,
          setFileUploadState,
          () => {
            if (onUnsupportedFile) {
              onUnsupportedFile()
            }
          },
          () => {
            if (onUnsupportedFile) {
              onUnsupportedFile()
            }
          },
          false // isUploadToSheet
        )
        */
      } catch (error) {
        console.error('Error uploading file:', error)
        setFileUploadState('idle')

        const errorMessage = `Error uploading ${file.name}`
        if (onUploadError) {
          onUploadError(errorMessage)
        }
      }
    },
    [
      environmentId,
      spaceId,
      fileUploads,
      calculateUploadProgress,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      onUploadError,
      onExtractionStart,
      onExtractionComplete,
      onUnsupportedFile,
    ]
  )

  // Set up event listeners for job events
  useEffect(() => {
    // TODO: TEMPORARY SOLUTION - Job event listeners are not yet setup
    // These should be re-enabled once we properly connect to job events
    /*
    const handleJobCreated = (event: any) => {
      if (
        event.context?.fileId === fileIdRef.current &&
        fileUploadStateRef.current === 'waiting'
      ) {
        setFileUploadState('extracting')
        if (onExtractionStart) {
          onExtractionStart()
        }
      }
    }

    const handleJobCompleted = (event: any) => {
      if (event.context?.fileId === fileIdRef.current) {
        setFileUploadState('idle')
        if (onExtractionComplete) {
          onExtractionComplete()
        }
      }
    }

    const handleJobFailed = (event: any) => {
      if (event.context?.fileId === fileIdRef.current) {
        setFileUploadState('idle')
        if (onExtractionError) {
          onExtractionError('Extraction failed')
        }
      }
    }

    // In a real implementation, you would set up event listeners here
    // For example, using a WebSocket or EventSource connection

    // Cleanup function
    return () => {
      // Clean up event listeners
    }
    */

    // No-op for now
    return () => {}
  }, [onExtractionStart, onExtractionComplete, onExtractionError])

  return {
    handleFileUpload,
    uploadProgress,
    fileName,
    fileUploadState,
    formatSpeed,
  }
}
